import React, { useState } from 'react';
import clsx from 'clsx';
import { Dialog, DialogActions, DialogContent } from '@mui/material';
import { Box, Button, Icons } from '@curves/ui-base';
import { GradientColorHeader } from '@Local/Components/Dialog/';
import { ensureArray } from '@Util/utils';
import { REFERENCE_KIND_CATEGORY } from '../CampaignEdit.config';

/**
 * 分類樹的單一列
 * - 有子分類時點箭頭展開收合；已加入可移除，上層已加入時子分類標示為已包含、不可再加
 */
const CategoryNode = ({ node, level, selectedKeys, isAncestorSelected, onAdd, onRemove }) => {
    const { saleCategoryID, saleCategoryName, children } = node;
    const hasChildren = Array.isArray(children) && children.length > 0;
    // 此節點是否展開，根分類預設展開
    const [isExpanded, setIsExpanded] = useState(level === 0);
    const isSelected = selectedKeys.includes(`${REFERENCE_KIND_CATEGORY}_${saleCategoryID}`);
    // 此節點對應的名單項目
    const item = { referenceKind: REFERENCE_KIND_CATEGORY, referenceID: saleCategoryID, displayName: saleCategoryName, isDeleted: false };

    // 已由上層包含：上層已加入且本身未加入，不可再操作
    const isIncluded = isAncestorSelected && !isSelected;

    // 依加入狀態決定右側顯示：已加入可移除、已由上層包含，或加入按鈕
    const renderAction = () => {
        if (isSelected) {
            return (
                <>
                    <span className="campaign-pick-action__status">
                        <Icons.Check fontSize="small" />
                        已加入
                    </span>
                    <Button size="small" variant="text" color="error" onClick={() => onRemove(item)}>
                        移除
                    </Button>
                </>
            );
        }
        if (isIncluded) return <span className="campaign-pick-action__hint">已由上層包含</span>;
        return (
            <Button size="small" variant="text" color="primary" onClick={() => onAdd(item)}>
                加入
            </Button>
        );
    };

    return (
        <>
            <Box
                className={clsx(
                    'campaign-category-tree__row',
                    level === 0 && 'campaign-category-tree__row--root',
                    level >= 2 && 'campaign-category-tree__row--deep',
                    isSelected && 'campaign-category-tree__row--selected',
                    isIncluded && 'campaign-category-tree__row--included',
                )}
            >
                {hasChildren ? (
                    <button type="button" className={clsx('campaign-category-tree__toggle', isExpanded && 'campaign-category-tree__toggle--expanded')} onClick={() => setIsExpanded(prev => !prev)}>
                        <Icons.KeyboardArrowRight fontSize="small" />
                    </button>
                ) : (
                    <span className="campaign-category-tree__toggle" />
                )}
                <Box className="campaign-category-tree__name">
                    {saleCategoryName}
                    {hasChildren && <span className="campaign-category-tree__count">{`${children.length} 項`}</span>}
                </Box>
                <Box className="campaign-pick-action">{renderAction()}</Box>
            </Box>
            {hasChildren && isExpanded && (
                <Box className="campaign-category-tree__children">
                    {children.map(child => (
                        <CategoryNode
                            key={child.saleCategoryID}
                            node={child}
                            level={level + 1}
                            selectedKeys={selectedKeys}
                            isAncestorSelected={isAncestorSelected || isSelected}
                            onAdd={onAdd}
                            onRemove={onRemove}
                        />
                    ))}
                </Box>
            )}
        </>
    );
};

/**
 * 分類選擇視窗
 * - 顯示分類 API 回傳的分類樹，可加入或移除任意層分類
 */
const CampaignCategoryDialog = ({ open, title, categoryTrees, selectedKeys, onAdd, onRemove, onClose }) => (
    <Dialog open={open} onClose={onClose} PaperProps={{ className: 'wx-65rem' }} maxWidth={false} fullWidth>
        <GradientColorHeader onClose={onClose}>{title}</GradientColorHeader>
        <DialogContent>
            {ensureArray(categoryTrees).length === 0 && <Box className="font-color-3">目前沒有可選擇的分類</Box>}
            <Box className="campaign-category-tree">
                {ensureArray(categoryTrees).map(root => (
                    <CategoryNode key={root.saleCategoryID} node={root} level={0} selectedKeys={selectedKeys} isAncestorSelected={false} onAdd={onAdd} onRemove={onRemove} />
                ))}
            </Box>
        </DialogContent>
        <DialogActions>
            <Button variant="contained" color="primary" onClick={onClose}>
                完成
            </Button>
        </DialogActions>
    </Dialog>
);

export default CampaignCategoryDialog;
